/************************************** THIRD-PARTY MODULES ***************************************/
import localforage from 'localforage';
import {javelinDataStore} from '@canimmunize/javelinscript';

/**************************************** PROJECT MODULES *****************************************/
import {
    getStoredPropsList,
    buildDbStorageKey,
    doKeepOnLogout,
    revertToValOnLogout,
    getRefPropObjects,
    RefPropObj,
} from './serialization-utils';

const dsFn = `delFromSerializablePropsList ::`;
const edpFn = `eraseDataPoint ::`;
const sdpFn = `setDataPoint ::`;

/********************************************* EXPORT *********************************************/
/**
 * Register handler that triggers delete of all non-protected keys on logout.
 *
 * @param {object} instance Instance to delete non-protected keys from
 * @param {string} storePrefix Prefixes all keys in local store (used for finding correct key)
 */
export function registerKeyDeletionOnLogout<T extends object>(instance: T, storePrefix: string) {
    /**
     * Register LOGOUT event handler
     * Iterate thru serializable props list & delete all from localForage unless protected.
     */
    javelinDataStore.jvlnsBroadcaster.subscribe('ev_LOGOUT', _ev =>
        delFromSerializablePropsList(instance, storePrefix).catch(err => {
            console.error(`registerKeyDeletionOnLogout: Del serializable props list failed:`, err);
        })
    );
}

/******************************************** HELPERS *********************************************/
/**
 * Delete all properties in the serializable props list from memory & local storage.
 * Exception: props with keepOnLogout set to true (don't delete them). e.g.:
 *     @storeLocally({keepOnLogout: true})
 *     propToKeep = 'defaultVal'
 *
 * Occurs on logout.
 *
 * @param {object} instance Instance to get serializable props list from (before deleting them)
 * @param {string} storePrefix Prefixes keys in local store (used to find correct key)
 */
function delFromSerializablePropsList(instance: object, storePrefix: string): Promise<boolean[][]> {
    return Promise.all(
        getStoredPropsList(instance).map(async (propKey: string) => {
            const newVal = revertToValOnLogout(instance, propKey);
            const keepOnLogout = doKeepOnLogout(instance, propKey);
            const refPropObjs = getRefPropObjects(instance);

            // Set data for prop to val at config->onLogout->revertTo, if revertTo isn't undefined
            if (typeof newVal !== 'undefined') {
                return refPropObjs
                    ? await setDataForEachRefProp(refPropObjs, propKey, storePrefix, newVal)
                    : ([await setDataPoint(instance, propKey, storePrefix, newVal)] as boolean[]);
            }

            // Don't delete data for prop if its onLogout->keep config val isn't set (to true)
            else if (keepOnLogout) {
            }
            // Delete data for prop if its onLogout->keep config val isn't set (to true)
            else if (!keepOnLogout) {
                // If refPropObjs present (i.e. instance is non-singleton), erase current
                // propKey for each referenced value; otherwise erase just the 1 data point
                return refPropObjs
                    ? await eraseDataPointForEachRefProp(refPropObjs, propKey, storePrefix)
                    : ([await eraseDataPoint(instance, propKey, storePrefix)] as boolean[]);
            }
        })
    );
}

/**
 * Set all data points to given value using reference property values. Occurs on logout.
 *
 * @param {string[]} refPropVals Reference property values, found via instance metadata
 * @param {object} instance Instance to set data for, from which props list was grabbed
 * @param {string} propKey Key to set value at (for all instances)
 * @param {any} valOnLogout Value to set at key for each instance
 *
 * @return {Promise<boolean[]>} Results of each property set attempt (true for each success)
 */
async function setDataForEachRefProp(
    refPropVals: RefPropObj[],
    propKey: string,
    storePrefix: string,
    valOnLogout: any
): Promise<boolean[]> {
    return Promise.all(
        refPropVals.map(
            async ({instance, refPropVal}: RefPropObj): Promise<boolean> =>
                setDataPoint(instance, propKey, storePrefix, refPropVal, valOnLogout)
        )
    );
}

/**
 * Set a single data point to the given value, for both the (in-memory) MobX
 * store & localforage (IndexedDB)
 *
 * @param {object} instance Class instance
 * @param {string} instanceKey Key on class instance
 * @param {string} storePrefix Added prefix for IndexedDB stored key
 * @param {any} valOnLogout Value to set key to
 *
 * @return {boolean} true if setting data succeeded
 */
async function setDataPoint(
    instance: object,
    instanceKey: string,
    storePrefix: string,
    valOnLogout: any,
    refPropVal?: string
): Promise<boolean> {
    const dbStorageKey = buildDbStorageKey(instance, instanceKey, storePrefix, refPropVal);

    // Delete property from in-browser store (localforage / IndexedDB)
    return localforage
        .setItem(dbStorageKey, valOnLogout)
        .then(_rmItem => {
            return true;
        })
        .catch(error => {
            console.error(`${sdpFn} ${instanceKey} not set in store + memory. ERROR:`, error);
            return false;
        });
}

/**
 * Erase all data points using reference property values. Occurs on logout.
 *
 * @param {string[]} refPropVals Reference property values, found via instance metadata
 * @param {object} instance Instance to delete data from, from which props list was grabbed
 * @param {string} propKey Key of property to erase (for all instances)
 *
 * @return {Promise<boolean[]>} Results of each property delete attempt (true for each success)
 */
async function eraseDataPointForEachRefProp(
    refPropVals: RefPropObj[],
    propKey: string,
    storePrefix: string
): Promise<boolean[]> {
    return Promise.all(
        refPropVals.map(
            async ({instance, refPropVal}: RefPropObj): Promise<boolean> =>
                eraseDataPoint(instance, propKey, storePrefix, refPropVal)
        )
    );
}

/**
 * Eliminate a single data point from both the (in-memory) MobX store & localforage (IndexedDB)
 *
 * @param {object} instance Class instance
 * @param {string} instanceKey Key on class instance
 * @param {string} storePrefix Added prefix for IndexedDB stored key
 *
 * @return {boolean} true if erasing succeeded
 */
async function eraseDataPoint(
    instance: object,
    instanceKey: string,
    storePrefix: string,
    refPropVal?: string
): Promise<boolean> {
    const dbStorageKey = buildDbStorageKey(instance, instanceKey, storePrefix, refPropVal);

    // Delete property from in-browser store (localforage / IndexedDB)
    return localforage
        .removeItem(dbStorageKey)
        .then(_rmItem => {
            return true;
        })
        .catch(error => {
            console.error(`${edpFn} ${instanceKey} not removed from store + memory. ERROR:`, error);
            return false;
        });
}
